import { useState, useEffect, useRef, useCallback } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { CallState, TwilioDeviceState, CallEntityType } from '@/types/twilio';
import { useApiClient } from '@api-client/context';
import { useStore } from '@hooks/useStore';
import axios from 'axios';

const TWILIO_MICROPHONE_PERMISSION_ERROR_CODE = 31401;

const isMicrophonePermissionError = (error: any): boolean => {
  // Check for microphone permission error (Twilio error code 31401)
  // Handle both string and number error codes, and PermissionDeniedError type
  const errorCode = error?.code;
  const errorName = error?.name || error?.constructor?.name || '';
  const errorMessage = error?.message || '';

  return (
    errorCode === TWILIO_MICROPHONE_PERMISSION_ERROR_CODE ||
    Number(errorCode) === TWILIO_MICROPHONE_PERMISSION_ERROR_CODE ||
    errorMessage.includes('(31401)')
  );
};

const getErrorMessage = (error: any): string => {
  const isPermissionError = isMicrophonePermissionError(error);

  if (isPermissionError) {
    return 'You need to give your browser permission to use your microphone. Please contact support if you need help on how to do this.';
  }

  return error?.message || 'An error occurred';
};

export function useTwilioVoice() {
  const api = useApiClient();
  const { authStore } = useStore();
  const { user } = authStore;
  const deviceRef = useRef<Device | null>(null);
  const currentCallRef = useRef<Call | null>(null);
  const incomingCallRef = useRef<Call | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const disconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // True while the device is registered under the user's stable identity for inbound calls.
  // The per-call teardown must leave that device alone or the user stops being reachable.
  const registeredForIncomingRef = useRef(false);

  const idleCallState = (): CallState => ({
    status: 'idle',
    phoneNumber: null,
    phoneCallId: null,
    callSid: null,
    duration: 0,
    error: null,
    entityId: null,
    entityType: null,
    callerName: null,
    direction: undefined,
    callGroupName: null,
    contactAvatarUrl: null
  });

  const [deviceState, setDeviceState] = useState<TwilioDeviceState>({
    isRegistered: false,
    isInitializing: false,
    error: null
  });

  const [isTransferring, setIsTransferring] = useState(false);
  const [callState, setCallState] = useState<CallState>({
    status: 'idle',
    phoneNumber: null,
    phoneCallId: null,
    callSid: null,
    duration: 0,
    error: null,
    entityId: null,
    entityType: null
  });

  const startDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    let seconds = 0;
    durationIntervalRef.current = setInterval(() => {
      seconds++;
      setCallState((prev) => ({ ...prev, duration: seconds }));
    }, 1000);
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const destroyDevice = useCallback(async () => {
    if (deviceRef.current) {
      try {
        await deviceRef.current.unregister();
        deviceRef.current.destroy();
        deviceRef.current = null;
        setDeviceState({
          isRegistered: false,
          isInitializing: false,
          error: null
        });
      } catch (error: any) {}
    }
  }, []);

  const initializeDevice = useCallback(
    async (token: string) => {
      if (deviceState.isInitializing) {
        return;
      }

      // Destroy existing device before creating a new one
      if (deviceRef.current) {
        await destroyDevice();
      }

      setDeviceState((prev) => ({ ...prev, isInitializing: true }));

      try {
        const device = new Device(token, {
          logLevel: 1,
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU]
        });

        device.on('registered', () => {
          setDeviceState({
            isRegistered: true,
            isInitializing: false,
            error: null
          });
        });

        device.on('error', (error) => {
          const errorMessage = getErrorMessage(error);
          setDeviceState((prev) => ({
            ...prev,
            isInitializing: false,
            error: errorMessage
          }));
          setCallState((prev) => ({
            ...prev,
            status: 'error',
            error: errorMessage
          }));
        });

        device.on('tokenWillExpire', async () => {
          // A device registered for inbound calls is long lived, so the token has to be renewed in
          // place. Letting it lapse silently drops the registration and the user stops ringing.
          try {
            const { data } = await api.twilio.getToken();
            device.updateToken(data.access_token);
          } catch (error) {
            console.error('Failed to refresh the Twilio token:', error);
          }
        });

        device.on('incoming', (call) => {
          incomingCallRef.current = call;
          currentCallRef.current = call;

          const callerName =
            call.customParameters?.get('callerName') ||
            call.customParameters?.get('name') ||
            null;
          const entityId =
            call.customParameters?.get('entityId') ||
            call.customParameters?.get('individualId') ||
            null;
          const entityTypeParam = call.customParameters?.get('entityType');
          const entityType =
            entityTypeParam === 'companies' ? 'companies' : 'individuals';

          setCallState({
            status: 'incoming',
            phoneNumber: call.parameters?.From || null,
            // Sent by the backend as a custom parameter. Needed to transfer the call, so it must
            // be kept rather than discarded.
            phoneCallId: call.customParameters?.get('phoneCallId') || null,
            callSid: call.parameters?.CallSid || null,
            duration: 0,
            error: null,
            entityId,
            entityType: entityId ? entityType : null,
            callerName,
            direction: 'inbound',
            callGroupName:
              call.customParameters?.get('callGroupName') ||
              call.customParameters?.get('call_group') ||
              null,
            contactAvatarUrl:
              call.customParameters?.get('avatarUrl') ||
              call.customParameters?.get('contactAvatarUrl') ||
              null
          });

          call.on('cancel', () => {
            incomingCallRef.current = null;
            currentCallRef.current = null;
            setCallState(idleCallState());
          });
        });

        await device.register();
        deviceRef.current = device;
      } catch (error: any) {
        console.error('Failed to initialize Twilio Device:', error);
        const errorMessage = getErrorMessage(error);
        setDeviceState({
          isRegistered: false,
          isInitializing: false,
          error: errorMessage
        });
      }
    },
    [api, deviceState.isInitializing, destroyDevice]
  );

  /**
   * Registers this browser under the user's stable identity so inbound calls can reach it.
   *
   * Unlike the per-call device used for outbound dialling, this one is long lived: it is created
   * once and kept registered, because a call can arrive at any time.
   */
  const registerForIncomingCalls = useCallback(async () => {
    if (registeredForIncomingRef.current || deviceState.isInitializing) {
      return;
    }

    try {
      const { data } = await api.twilio.getToken();
      registeredForIncomingRef.current = true;
      await initializeDevice(data.access_token);
    } catch (error) {
      registeredForIncomingRef.current = false;
      console.error('Failed to register for incoming calls:', error);
    }
  }, [api, deviceState.isInitializing, initializeDevice]);

  useEffect(() => {
    if (!user?.feature_flags?.inbound_calling || !user?.canMakePhoneCalls) {
      return;
    }

    registerForIncomingCalls();
  }, [
    user?.feature_flags?.inbound_calling,
    user?.canMakePhoneCalls,
    registerForIncomingCalls
  ]);

  const initiateCall = useCallback(
    async (
      entityId: string,
      phoneNumber: string,
      callerName: string,
      entityType: CallEntityType
    ) => {
      // Check if user has permission to make phone calls
      if (!user?.canMakePhoneCalls) {
        setCallState((prev) => ({
          ...prev,
          status: 'error',
          error: 'You do not have permission to make phone calls'
        }));
        return;
      }

      try {
        setCallState({
          status: 'initiating',
          phoneNumber,
          phoneCallId: null,
          callSid: null,
          duration: 0,
          error: null,
          entityId,
          entityType,
          callerName: callerName || user?.name,
          direction: 'outbound'
        });

        const callResponse = await api.twilio.initiateCall(
          entityId,
          entityType
        );

        // A device already registered for inbound calls keeps its identity: replacing it would
        // unregister the user and stop them being reachable. Only fall back to the per-call token
        // when there is no such registration.
        if (!registeredForIncomingRef.current || !deviceRef.current) {
          await initializeDevice(callResponse?.data?.access_token);
        }

        if (!deviceRef.current) {
          setCallState((prev) => ({
            ...prev,
            status: 'error',
            error: 'Device not initialized'
          }));
          return;
        }

        setCallState((prev) => ({
          ...prev,
          status: 'connecting',
          phoneCallId: callResponse?.data?.phone_call?.id?.toString() || null,
          callerName: callResponse?.data?.phone_call?.name || null
        }));

        const call = await deviceRef.current.connect({
          params: {
            To: callResponse?.data?.phone_call?.to || '',
            // A device registered under the user's identity no longer carries the call id in that
            // identity, so the backend is told which call is being placed.
            phoneCallId: callResponse?.data?.phone_call?.id?.toString() || ''
          }
        });

        currentCallRef.current = call;

        // 'ringing' means the far end has been notified but has not picked up yet. The SDK has no
        // 'connect' event - 'accept' is what fires once the call is actually open - so the two
        // handlers below are what move an outbound call from ringing to connected.
        call.on('ringing', () => {
          setCallState((prev) => ({ ...prev, status: 'ringing' }));
        });

        call.on('accept', () => {
          setCallState((prev) => ({ ...prev, status: 'connected' }));
          startDurationTimer();
        });

        call.on('disconnect', (event) => {
          stopDurationTimer();

          // Clear any existing timeout
          if (disconnectTimeoutRef.current) {
            clearTimeout(disconnectTimeoutRef.current);
            disconnectTimeoutRef.current = null;
          }

          setCallState({
            status: 'disconnected',
            phoneNumber: null,
            phoneCallId: null,
            callSid: null,
            duration: 0,
            error: null,
            entityId: null,
            entityType: null
          });
          currentCallRef.current = null;

          disconnectTimeoutRef.current = setTimeout(() => {
            disconnectTimeoutRef.current = null;
            if (!registeredForIncomingRef.current) {
              destroyDevice();
            }
            setCallState((prev) => ({ ...prev, status: 'idle' }));
          }, 4500);
        });

        call.on('error', (error) => {
          console.error('Call Error:', error, callState);

          // Clear the disconnect timeout so it doesn't overwrite the error state
          if (disconnectTimeoutRef.current) {
            clearTimeout(disconnectTimeoutRef.current);
            disconnectTimeoutRef.current = null;
          }

          function setBrowserPermissionError() {
            setCallState((prev) => ({
              ...prev,
              status: 'browserPermissionError',
              error:
                'You need to give your browser permission to use your microphone. Please contact support if you need help on how to do this.'
            }));
          }

          if (isMicrophonePermissionError(error)) {
            setBrowserPermissionError();
            setTimeout(() => {
              setBrowserPermissionError();
            }, 5000);
            return;
          }
          stopDurationTimer();

          setCallState((prev) => ({
            ...prev,
            status: 'error',
            error: error.message || 'An error occurred'
          }));
        });

        call.on('reject', () => {
          stopDurationTimer();
          setCallState((prev) => ({
            ...prev,
            status: 'error',
            error: 'Call was rejected'
          }));
        });

        call.on('cancel', () => {
          stopDurationTimer();
          setCallState({
            status: 'disconnected',
            phoneNumber: null,
            phoneCallId: null,
            callSid: null,
            duration: 0,
            error: null,
            entityId: null,
            entityType: null
          });
          setTimeout(() => {
            setCallState((prev) => ({ ...prev, status: 'idle' }));
          }, 1000);
        });
      } catch (error: any) {
        console.error('Axios error:', error);
        if (axios.isAxiosError(error)) {
          setCallState((prev) => ({
            ...prev,
            status: 'error',
            error: error.response?.data?.error || 'Failed to initiate call'
          }));
        }
      }
    },
    [
      api,
      initializeDevice,
      startDurationTimer,
      stopDurationTimer,
      user?.canMakePhoneCalls,
      user?.name
    ]
  );

  const acceptIncomingCall = useCallback(() => {
    const call = incomingCallRef.current;
    if (!call) {
      setCallState((prev) => ({
        ...prev,
        status: 'connected',
        direction: 'inbound'
      }));
      return;
    }

    call.accept();
    incomingCallRef.current = null;
    currentCallRef.current = call;

    setCallState((prev) => ({
      ...prev,
      status: 'connecting',
      direction: 'inbound'
    }));

    call.on('accept', () => {
      setCallState((prev) => ({ ...prev, status: 'connected' }));
      startDurationTimer();
    });

    call.on('disconnect', () => {
      stopDurationTimer();
      incomingCallRef.current = null;
      currentCallRef.current = null;
      setCallState({
        ...idleCallState(),
        status: 'disconnected'
      });
      disconnectTimeoutRef.current = setTimeout(() => {
        disconnectTimeoutRef.current = null;
        if (!registeredForIncomingRef.current) {
          destroyDevice();
        }
        setCallState((prev) => ({ ...prev, status: 'idle' }));
      }, 4500);
    });

    call.on('error', (error) => {
      stopDurationTimer();
      setCallState((prev) => ({
        ...prev,
        status: 'error',
        error: error.message || 'An error occurred'
      }));
    });
  }, [destroyDevice, startDurationTimer, stopDurationTimer]);

  const rejectIncomingCall = useCallback(() => {
    const call = incomingCallRef.current;
    if (call) {
      call.reject();
    }
    incomingCallRef.current = null;
    currentCallRef.current = null;
    setCallState(idleCallState());
  }, []);

  /**
   * Hands the current call to another user.
   *
   * Only inbound calls can be transferred: the API rejects outbound ones because they are recorded,
   * and ending the leg would split one conversation into two recordings and summaries.
   *
   * There is nothing to tear down locally on success - the backend ends this browser's leg, so the
   * existing disconnect handler returns the UI to idle on its own.
   */
  const transferCall = useCallback(
    async (userId: number) => {
      if (!callState.phoneCallId) {
        setCallState((prev) => ({
          ...prev,
          error: 'This call cannot be transferred.'
        }));
        return;
      }

      setIsTransferring(true);

      try {
        await api.phoneCall.transfer(callState.phoneCallId, userId);
      } catch (error: any) {
        setCallState((prev) => ({
          ...prev,
          error:
            error?.response?.data?.error ||
            'The call could not be transferred.'
        }));
      } finally {
        setIsTransferring(false);
      }
    },
    [api, callState.phoneCallId]
  );

  /**
   * Sends DTMF tones on the live call, so the user can work through a switchboard menu.
   *
   * Twilio relays these to the far end exactly as if the key had been pressed on a handset, so no
   * TwiML or backend involvement is needed. Accepts more than one digit, which lets a whole
   * sequence be sent at once.
   */
  const sendDigits = useCallback((digits: string) => {
    const call = currentCallRef.current;

    if (!call) {
      return;
    }

    call.sendDigits(digits);
  }, []);

  const terminateCall = useCallback(() => {
    console.log('terminateCall');

    // Clear disconnect timeout if it exists
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }

    if (!currentCallRef.current) {
      console.warn('No active call to terminate');
      setCallState((prev) => ({
        ...prev,
        status: 'idle',
        phoneNumber: null,
        phoneCallId: null,
        callSid: null,
        duration: 0,
        error: null,
        entityId: null,
        entityType: null
      }));
      return;
    }

    try {
      // Disconnect the call on the frontend
      currentCallRef.current.disconnect();

      stopDurationTimer();
      setCallState({
        status: 'disconnected',
        phoneNumber: null,
        phoneCallId: null,
        callSid: null,
        duration: 0,
        error: null,
        entityId: null,
        entityType: null
      });

      currentCallRef.current = null;

      setTimeout(() => {
        setCallState((prev) => ({ ...prev, status: 'idle' }));
      }, 2000);
    } catch (error: any) {
      console.error('Failed to terminate call:', error);
      setCallState((prev) => ({
        ...prev,
        status: 'error',
        error: error.message || 'Failed to terminate call'
      }));
    }
  }, [stopDurationTimer]);

  const getCallStatusText = useCallback((): string => {
    switch (callState.status) {
      case 'incoming':
        return 'Incoming call...';
      case 'initiating':
        return 'Initiating...';
      case 'connecting':
        return 'Connecting...';
      case 'ringing':
        return 'Ringing...';
      case 'connected':
        return 'Connected';
      case 'disconnected':
        return 'Call ended';
      case 'error':
        return callState.error || 'Error occurred';
      default:
        return '';
    }
  }, [callState.status, callState.error]);

  useEffect(() => {
    return () => {
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
      }
      if (currentCallRef.current) {
        currentCallRef.current.disconnect();
      }
      registeredForIncomingRef.current = false;
      destroyDevice();
      stopDurationTimer();
    };
  }, [stopDurationTimer, destroyDevice]);

  return {
    deviceState,
    callState,
    initiateCall,
    terminateCall,
    acceptIncomingCall,
    rejectIncomingCall,
    transferCall,
    isTransferring,
    sendDigits,
    // Tones only carry once the far end has picked up; a ringing call has nothing to send them to.
    canSendDigits: callState.status === 'connected',
    // A transfer moves the caller to somebody else, so it only applies to a connected inbound call
    // that the backend has given us an id for.
    canTransferCall:
      callState.direction === 'inbound' &&
      callState.status === 'connected' &&
      !!callState.phoneCallId,
    getCallStatusText,
    initializeDevice
  };
}
